import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'

import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import Clickable from 'components/Common/Clickable'
import TextButton from 'LuxKit/Button/TextButton'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import { getTotalRoomsString, prettyDates } from 'components/Search/utils'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from '../../../actions/NavigationActions'
import { connect } from 'react-redux'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { useHistory } from 'react-router'
import { nonNullable } from 'lib/array/arrayUtils'

interface MappedStateProps {
  windowSearch: string
}

interface Props {
  className?: string
  onEdit: () => void
  location?: string
  isDestinationPage?: boolean
  switchView?: {
    label: string
    view: 'map' | 'list'
    URL: string
    goBack?: boolean
  }
}

const StyledContainer = styled(Clickable)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${rem(8)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  width: 100%;

  &.landing-page-test {
    padding: ${rem(20)} ${rem(25)} ${rem(20)} ${rem(25)};
  }
`

const StyledDetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${rem(2)};
  &.map-view {
    align-items: center;
  }
`

const StyledDescriptionsSection = styled.div`
  display: flex;
  column-gap: ${rem(8)};
`

// The end containers need to be the same width so that the text is centered
const EndContainers = styled.div`
  width: ${rem(40)};
  display: flex;
  justify-content: center;
  align-items: center;
`

function HotelSearchHeaderBar(props: Props & MappedStateProps) {
  const {
    onEdit,
    className,
    location,
    isDestinationPage,
    switchView,
    windowSearch,
  } = props

  const {
    checkinDate,
    checkoutDate,
    occupancies,
    dateSearchOptionId,
    flexibleMonths,
    flexibleNights,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)
  const dispatch = useAppDispatch()

  const { destinationName } = parseSearchString(windowSearch)

  const searchBarDescription = useMemo(() => {
    const bulletPointSeparator = '\u2022'
    if (dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME) {
      return [
        'Anytime',
      ]
    }
    const datesDetails = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE ?
        prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights) :
        prettyDates(checkinDate, checkoutDate)
    let occupancyWithRoomDetails: string | undefined = undefined
    if (occupancies) {
      const occupancyDetails = formatOccupantsAsGuests(...occupancies)
      const roomsDetails = getTotalRoomsString(occupancies)
      occupancyWithRoomDetails = `${occupancyDetails} ${roomsDetails}`
    }

    return nonNullable([
      datesDetails,
      bulletPointSeparator,
      occupancyWithRoomDetails,
    ])
  }, [dateSearchOptionId, userSelectedFlexibleMonths, flexibleMonths, flexibleNights, checkinDate, checkoutDate, occupancies])

  const history = useHistory()

  const onListViewClick = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!switchView) return
    e.stopPropagation()

    // If you are navigating back from the map view you should be taken back to where you have scrolled
    if (switchView.goBack) {
      history.goBack()
      return
    }

    dispatch(pushWithRegion(switchView.URL))
  }, [switchView, dispatch, history])

  return (
    <StyledContainer className={cn(className, { 'landing-page-test': isDestinationPage })} onClick={onEdit}>
      { switchView?.view === 'list' &&
        <EndContainers onClick={onListViewClick}>
          <LineArrowLeftIcon />
        </EndContainers>}
      <StyledDetailsSection className={cn({ 'map-view': switchView?.view === 'list' })}>
        <Heading variant="heading6">{destinationName ?? location ?? 'Map area'}</Heading>
        <StyledDescriptionsSection>
          {searchBarDescription.map((description, index) => (
            <Caption key={index} variant="large">
              {description}
            </Caption>
          ))}
        </StyledDescriptionsSection>
      </StyledDetailsSection>
      <EndContainers>
        <TextButton nonInteractable kind="tertiary">Edit</TextButton>
      </EndContainers>
    </StyledContainer>
  )
}

export default connect<MappedStateProps, {}, Props, App.State>((state): MappedStateProps => {
  return {
    windowSearch: state.router.location.search,
  }
})(HotelSearchHeaderBar)
